import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import { TableCell, TableRow } from '@mui/material';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { SearchIcon } from '../../../constants/icons';
import TableComponent from '../../../components/Table/TableComponent';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import ChartButton from '../../../components/Button/ChartButton';
import { Hemoglobin, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const HemoglobinForm: React.FC<{ patientId: string }> = ({ patientId }) => {
    const [hemoglobinRecords, setHemoglobinRecords] = useState<Hemoglobin[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [editRecord, setEditRecord] = useState<Hemoglobin | null>(null);
    const [showChart, setShowChart] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState<Hemoglobin | null>(null);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    useEffect(() => {
        fetchHemoglobinRecords(dateRange.startDate, dateRange.endDate);
    }, [page, rowsPerPage, searchTerm]);

    const fetchHemoglobinRecords = async (startDate: string = '', endDate: string = '') => {
        setHemoglobinRecords([])
        setLoading(true);
        
        try {
            const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
                params: {
                    limit: rowsPerPage,
                    page: page + 1,
                    from: startDate || '',
                    to: endDate || '',
                    order_by: 'details->date',
                    dir: 2,
                    user_id: patientId,
                    slug: 'hemoglobin',
                    searchkey: searchTerm || undefined,
                },
                headers: getAxiosConfig().headers,
            });

            const { vitals } = response.data.data;
            const total = response.data.data.pagination!.total;

            if (Array.isArray(vitals) && vitals.length > 0) {
                const formattedData: Hemoglobin[] = vitals.map((vital) => ({
                    id: vital.id,
                    user_id: vital.user_id,
                    consult_id: vital.consult_id,
                    peripheral_id: vital.peripheral_id,
                    slug: vital.slug,
                    details: {
                        range_code: vital.details.range_code,
                        hemoglobinFlag: vital.details.hemoglobinFlag,
                        id: vital.id, // Assuming this is the correct ID
                        date: vital.details.date,
                        time: vital.details.time,
                        hemoglobin: vital.details.hemoglobin,
                        hemoglobinFlagColor: vital.details.hemoglobinFlagColor,
                    },
                    freeze: vital.freeze, // Assign a default value or fetch if needed
                    document: null, // Assign a default value or fetch if needed
                }));

                setHemoglobinRecords(formattedData);
                setTotalRecords(total);
            }

        } catch (error) {
            console.error('Error fetching hemoglobin records:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setEditRecord(null);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        fetchHemoglobinRecords(dateRange.startDate, dateRange.endDate);
    };

    const handleDialogSave = (newRecord: Hemoglobin) => {
        try {
            if (editRecord) {
                setHemoglobinRecords(prev => prev.map(record => (record.id === editRecord.id ? newRecord : record)));
                showSnackbar('Hemoglobin updated successfully!', 'success');
            } else {
                setHemoglobinRecords(prev => [...prev, newRecord]);
                showSnackbar('Hemoglobin saved successfully!', 'success');
            }
        } catch (error) {
            console.error('Error saving the record:', error);
            showSnackbar('Failed to save the record.', 'error'); // Handle error
        } finally {
            setOpenDialog(false);
            fetchHemoglobinRecords(); // Refresh data after saving
        }
    }

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ startDate: '', endDate: '' });
        setPage(0); // Reset to the first page
        fetchHemoglobinRecords(); // Fetch all records again
        setFiltersApplied(false);// Hide the clear button
    };

    const renderRow = (item: Hemoglobin, index: number) => (
        <TableRow key={index}>
            <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{(item.details.date)}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.time}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.hemoglobin} </TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                <span
                    style={{
                        backgroundColor: item.details.range_code,
                        padding: '4px 8px',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                    }}
                >
                    {item.details.hemoglobinFlag}
                </span>
            </TableCell>
        </TableRow>
    );

    const chartData = {
        labels: hemoglobinRecords.map(record => record.details.date),
        datasets: [
            {
                label: 'Hemoglobin (g/dL)',
                data: hemoglobinRecords.map(record => record.details.hemoglobin),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        animations: {
            tension: {
                easing: 'linear',
                from: 0.4, // Start tension
                to: 0.0,   // End tension
                // loop: true,
            },
        },
    };
    return (
        <div>
            <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
                <div className="flex flex-grow space-x-2 gap-2">
                    <DateRangePicker
                        startDateId="Hemoglobin_Start_Date"
                        endDateId="Hemoglobin_End_Date"
                        onChange={(dates: { startDate: string; endDate: string }) => {
                            setDateRange(dates);
                            setFiltersApplied(true);
                        }}
                        value={dateRange}
                    />
                    <IconButton
                        icon={<SearchIcon />}
                        onClick={() => {
                            fetchHemoglobinRecords(dateRange.startDate, dateRange.endDate);
                            setFiltersApplied(true); // Mark filters as applied when search is clicked
                        }}
                    />

                    {filtersApplied && (
                        <IconButton
                            icon={<ClearIcon />}
                            onClick={handleClearFilters}
                        />
                    )}
                </div>

                <div className="flex justify-end space-x-4">
                    <ChartButton
                        text={showChart ? 'List' : 'Chart'}
                        onClick={() => setShowChart(prev => !prev)}
                    />
                   </div>

            </div>

            {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
                    <TableComponent
                        headers={['S.No', 'Date', 'Time', 'Value (g/dL)', 'Result']}
                        data={hemoglobinRecords}
                        loading={loading}
                        renderRow={renderRow} error={''} />
                 
                </>
            )}
            <TablePaginationComponent
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />

        </div>
    );
};

export default HemoglobinForm;
